import "@/App.css";
import { Plane, Users, DollarSign, MessageSquare, Radio, PlaneTakeoff, Link2, ShieldCheck, ExternalLink } from "lucide-react";
import { useDashboardData } from "@/hooks/use-dashboard-data";

const BILLION = 1e9;
const MILLION = 1e6;
const THOUSAND = 1e3;

const fmtNum = (n) => (Number(n) || 0).toLocaleString();
const fmtMoney = (n) => {
  n = Number(n) || 0;
  if (Math.abs(n) >= BILLION) return `$${(n / BILLION).toFixed(1)}B`;
  if (Math.abs(n) >= MILLION) return `$${(n / MILLION).toFixed(1)}M`;
  if (Math.abs(n) >= THOUSAND) return `$${(n / THOUSAND).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
};

const getStatusLabel = (online, botInfo) => {
  if (online) return `LIVE · ${botInfo?.guild || ""}`;
  if (botInfo?.error) return `ERROR: ${botInfo.error}`;
  return "CONNECTING…";
};

const LINKS = [
  { label: "Vote for Squawk 7700/7600", url: "https://top.gg/bot/1493918718110204054" },
  { label: "Join the Discord Server", url: "https://discord.gg/squawkbot" },
  { label: "Play Airline Tycoon", url: "https://airlinetycoon.xyz/" },
  { label: "Privacy Policy", url: "#privacy-policy" },
];

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="stat-card" data-testid={testid} style={{ "--accent": accent }}>
    <div className="stat-icon"><Icon size={22} strokeWidth={2.2} /></div>
    <div className="stat-value" data-testid={`${testid}-value`}>{value}</div>
    <div className="stat-label">{label}</div>
  </div>
);

const BotCard = ({ name, tagline, online, metrics, inviteUrl, errorMessage, testid }) => (
  <section className="bot-card" data-testid={testid}>
    <div className={`bot-status-pill ${online ? "on" : "off"}`} data-testid={`${testid}-status`}>
      <Radio size={13} />
      {online ? "ONLINE" : "OFFLINE"}
    </div>
    <h2 className="bot-name">{name}</h2>
    <p className="bot-tagline">{tagline}</p>
    {!online && errorMessage ? (
      <div className="bot-error-note" data-testid={`${testid}-error`}>⚠ {errorMessage}</div>
    ) : null}
    <div className="bot-metrics">
      {metrics.map((m, i) => (
        <div
          key={m.slug}
          className={`bot-metric ${metrics.length % 2 === 1 && i === metrics.length - 1 ? "full" : ""}`}
          data-testid={`${testid}-${m.slug}`}
        >
          <span className="bot-metric-label">{m.label}</span>
          <span className="bot-metric-value">{m.value != null ? fmtNum(m.value) : "—"}</span>
        </div>
      ))}
    </div>
    {inviteUrl ? (
      <a className="bot-invite-btn" href={inviteUrl} target="_blank" rel="noreferrer" data-testid={`${testid}-invite`}>
        Invite Bot <ExternalLink size={14} />
      </a>
    ) : (
      <div className="bot-private-note" data-testid={`${testid}-private`}>Private bot — not invitable</div>
    )}
  </section>
);

const DashboardHeader = ({ online, bot }) => (
  <header className="atc-header">
    <div className="brand">
      <Plane size={26} className="brand-plane" />
      <div>
        <h1>SQUAWK &amp; AIRLINE TYCOON</h1>
        <span>Discord Bot Control Center</span>
      </div>
    </div>
    <div className={`status-pill ${online ? "on" : "off"}`} data-testid="bot-status-pill">
      <Radio size={14} />
      {getStatusLabel(online, bot)}
    </div>
  </header>
);

const DiscordWidgetPanel = () => (
  <div className="widget-panel" data-testid="discord-widget-panel">
    <div className="panel-head"><Radio size={16} /> Discord Server Preview</div>
    <div className="widget-frame-wrap">
      <iframe
        src="https://discord.com/widget?id=1485992638900338818&theme=dark"
        width="100%"
        height="330"
        allowtransparency="true"
        frameBorder="0"
        sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-scripts"
        title="Official Squawk Server widget"
        data-testid="discord-widget-iframe"
      />
    </div>
  </div>
);

const LinksPanel = () => (
  <div className="links-panel" data-testid="links-panel">
    <div className="panel-head"><Link2 size={16} /> Links</div>
    <ul className="links-list">
      {LINKS.map((l) => (
        <li key={l.label}>
          <a href={l.url} target={l.url.startsWith("#") ? undefined : "_blank"} rel="noreferrer" data-testid={`link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
            {l.label}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

const LiveStatsGrid = ({ stats }) => (
  <section className="stats-grid" data-testid="stats-grid">
    <StatCard icon={Users} label="Total Players" accent="#38bdf8"
      value={stats ? fmtNum(stats.players) : "—"} testid="stat-players" />
    <StatCard icon={Plane} label="Planes Purchased" accent="#f59e0b"
      value={stats ? fmtNum(stats.planes) : "—"} testid="stat-planes" />
    <StatCard icon={PlaneTakeoff} label="Total Flights" accent="#a78bfa"
      value={stats ? fmtNum(stats.flights) : "—"} testid="stat-flights" />
    <StatCard icon={DollarSign} label="Money Spent" accent="#34d399"
      value={stats ? fmtMoney(stats.money_spent) : "—"} testid="stat-money" />
  </section>
);

const InGameChatPanel = ({ chat }) => (
  <section className="panel" data-testid="chat-panel">
    <div className="panel-head"><MessageSquare size={16} /> In-Game Chat</div>
    <div className="chat-feed">
      {chat.length === 0 && <div className="chat-empty">No messages yet — chat will appear here and in #game-chat.</div>}
      {chat.map((m) => (
        <div key={m.id} className="chat-row" data-testid="chat-message">
          <span className="chat-user">{m.username}</span>
          <span className="chat-text">{m.message}</span>
        </div>
      ))}
    </div>
  </section>
);

const LiveDataSourcePanel = ({ stats }) => (
  <section className="panel" data-testid="source-panel">
    <div className="panel-head"><Radio size={16} /> Live Data Source</div>
    <p className="hint">
      The bot polls your game directly — no game-side setup needed. Stats refresh into the
      locked voice channels; new chat posts to <code>#game-chat</code>.
    </p>
    <div className="src-row" data-testid="source-stats">
      <span className="src-label">Stats</span>
      <a className="src-url" href="https://airlinetycoon.xyz/api/public/stats" target="_blank" rel="noreferrer">airlinetycoon.xyz/api/public/stats</a>
    </div>
    <div className="src-row" data-testid="source-chat">
      <span className="src-label">Chat</span>
      <a className="src-url" href="https://airlinetycoon.xyz/api/public/chat" target="_blank" rel="noreferrer">airlinetycoon.xyz/api/public/chat</a>
    </div>
    <div className="src-grid">
      <div className="src-mini"><span>Online Now</span><b data-testid="online-now">{stats ? fmtNum(stats.online_now) : "—"}</b></div>
      <div className="src-mini"><span>Stats Refresh</span><b>~6 min</b></div>
      <div className="src-mini"><span>Chat Poll</span><b>15 s</b></div>
    </div>
  </section>
);

const GameDataSection = ({ stats, chat }) => (
  <section className="game-section" data-testid="game-section">
    <div className="sub">Airline Tycoon — Live Game Data</div>
    <LiveStatsGrid stats={stats} />
    <div className="columns">
      <InGameChatPanel chat={chat} />
      <LiveDataSourcePanel stats={stats} />
    </div>
  </section>
);

const PrivacyFooter = ({ bot, squawkBot }) => (
  <footer className="atc-footer" id="privacy-policy" data-testid="privacy-policy">
    <div className="privacy-box">
      <div className="panel-head"><ShieldCheck size={16} /> Privacy &amp; Data Usage</div>

      <h3 className="privacy-subhead">What We Collect</h3>
      <p className="hint" style={{ margin: 0, textAlign: "left" }}>
        Our bots only process and store the minimum information required for their features.
        Where necessary, this may include Discord server, channel, and user IDs for bot
        configuration and functionality.
      </p>

      <h3 className="privacy-subhead">Squawk 7600 &amp; 7700</h3>
      <p className="hint" style={{ margin: 0, textAlign: "left" }}>
        The Squawk bot provides aviation emergency alerts and aircraft-related commands. It
        maintains anonymous, aggregate counters for statistics such as the total number of
        commands used, Squawk 7600/7700 alerts sent, servers using the bot, and combined members
        across those servers.
      </p>
      <p className="hint" style={{ margin: 0, textAlign: "left" }}>
        Command usage is counted globally only. For example, when any user runs a command, the
        overall counter increases by one. We do not maintain or publish statistics showing which
        user ran a command, what individual users use most frequently, or how many commands a
        particular user has used.
      </p>
      <p className="hint" style={{ margin: 0, textAlign: "left" }}>
        Individual member lists from servers using the bot are not published. The only member
        preview displayed on this website is for our own official Discord server and is provided
        directly through Discord's public Server Widget.
      </p>

      <h3 className="privacy-subhead">Airline Tycoon</h3>
      <p className="hint" style={{ margin: 0, textAlign: "left" }}>
        The Airline Tycoon bot connects Discord with the Airline Tycoon game. Information
        required for game accounts and features may be associated with a Discord user ID where
        necessary for the game to function.
      </p>

      <h3 className="privacy-subhead">What We Don't Do</h3>
      <p className="hint" style={{ margin: 0, textAlign: "left" }}>
        We do not sell user data, use Discord data for advertising or profiling, or publish
        private member information. Public bot statistics are aggregate totals and are not
        intended to identify or track individual Discord users.
      </p>

      <h3 className="privacy-subhead">Data Removal</h3>
      <p className="hint" style={{ margin: 0, textAlign: "left" }}>
        Questions about stored data or requests for deletion can be made by contacting the bot
        owner through our official support Discord server.
      </p>
    </div>
    <div style={{ marginTop: 18 }}>
      Voice channel names refresh every ~6 minutes (Discord rate limit) · Bots: {bot?.bot_name || "—"} &amp; {squawkBot?.botName || "—"}
    </div>
  </footer>
);

function App() {
  const { stats, bot, squawkBot, chat, online, squawkOnline, squawkMetrics, airlineMetrics } = useDashboardData();

  return (
    <div className="atc-root" data-testid="app-root">
      <div className="grain" />
      <DashboardHeader online={online} bot={bot} />

      <section className="bot-grid" data-testid="bot-grid">
        <BotCard
          testid="bot1-card"
          name="Squawk 7600 & 7700"
          tagline="Live aviation emergency alerts, aircraft lookup & the airline game"
          online={squawkOnline}
          metrics={squawkMetrics}
          errorMessage={squawkBot?.error}
          inviteUrl="https://top.gg/bot/1493918718110204054"
        />

        <div className="bot-middle" data-testid="bot-middle">
          <DiscordWidgetPanel />
          <LinksPanel />
        </div>

        <BotCard
          testid="bot2-card"
          name="Airline Tycoon Bot"
          tagline="Mirrors live game chat & stats from airlinetycoon.xyz into Discord"
          online={online}
          metrics={airlineMetrics}
          inviteUrl={null}
        />
      </section>

      <GameDataSection stats={stats} chat={chat} />
      <PrivacyFooter bot={bot} squawkBot={squawkBot} />
    </div>
  );
}

export default App;
